import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMenu } from '../../contexts/MenuContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import DropdownMenu from './DropdownMenu';
import {
  Sun,
  Moon,
  LogOut,
  ChevronDown,
  Menu,
  X
} from 'lucide-react';

// Importar logo
import logoMafy from '../../assets/images/logo-mafy.svg';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const {
    menuConfig,
    mobileMenuOpen,
    toggleMobileMenu,
    closeMenus,
    handleMenuClick
  } = useMenu();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    closeMenus();
    setIsUserMenuOpen(false);
  };

  const toggleUserMenu = (e) => {
    e.stopPropagation();
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleThemeToggle = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const handleNavigation = (path) => {
    navigate(path);
    closeMenus();
    setIsUserMenuOpen(false);
  };

  // Función mejorada para manejar clicks en menús
  const handleMenuItemClick = (item) => {
    console.log('Menu item clicked:', item); // Para debug
    if (typeof handleMenuClick === 'function') {
      handleMenuClick(item);
    }
    if (item.path) {
      handleNavigation(item.path);
    }
  };

  // Cerrar dropdown de usuario cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getUserRoleDisplay = () => {
    if (!user?.role) return 'Usuario';

    switch (user.role) {
      case 'SuperAdmin':
        return 'Super Administrador';
      case 'Admin':
        return 'Administrador';
      case 'Usuario':
        return 'Usuario';
      default:
        return user.role;
    }
  };

  const getUserName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email?.split('@')[0] || 'Usuario';
  };

  const getUserAvatar = () => {
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <nav className="bg-primary-600 dark:bg-primary-700 shadow-lg border-b border-primary-700 dark:border-primary-600 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Brand/Logo */}
          <div
            className="flex items-center space-x-3 cursor-pointer group flex-shrink-0"
            onClick={() => handleNavigation('/dashboard')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleNavigation('/dashboard');
              }
            }}
          >
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center overflow-hidden group-hover:bg-white/20 transition-colors duration-200">
              <img
                src={logoMafy}
                alt="Mafy"
                className="w-6 h-6 filter brightness-0 invert"
              />
            </div>
            <span className="text-xl font-bold text-white group-hover:text-accent-200 transition-colors duration-200 whitespace-nowrap">
              Mafy
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1 flex-1 justify-center">
            {menuConfig && menuConfig.length > 0 && menuConfig.map((menu) => (
              <div key={menu.id} className="relative group">  {/* Aquí está la clase group */}
                <DropdownMenu
                  menu={menu}
                  onMenuClick={handleMenuItemClick}
                  textColor="text-white"
                  hoverColor="hover:text-accent-200"
                  bgHover="hover:bg-white/10"
                />
              </div>
            ))}
          </div>

          {/* User Menu & Mobile Button */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            {/* User Menu Desktop */}
            <div className="relative" ref={userMenuRef}>
              <button
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${isUserMenuOpen
                    ? 'bg-white/20 text-white'
                    : 'text-white hover:text-accent-200 hover:bg-white/10'
                  }`}
                onClick={toggleUserMenu}
                title="Opciones de usuario"
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
              >
                <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                  {getUserAvatar()}
                </div>
                <div className="hidden md:block text-left min-w-0">
                  <div className="text-sm font-medium text-white truncate">{getUserName()}</div>
                  <div className="text-xs text-white/70 truncate">{getUserRoleDisplay()}</div>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isUserMenuOpen ? 'rotate-180' : ''
                  }`} />
              </button>

              {/* User Dropdown */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 animate-fade-in">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-accent-500 rounded-full flex items-center justify-center text-white text-lg font-medium flex-shrink-0">
                        {getUserAvatar()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {getUserName()}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user?.email}
                        </div>
                        <div className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                          {getUserRoleDisplay()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="py-1">
                    <button
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                      onClick={handleThemeToggle}
                      title={theme === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
                    >
                      {theme === 'dark' ? (
                        <Sun className="w-4 h-4 text-yellow-500" />
                      ) : (
                        <Moon className="w-4 h-4 text-blue-500" />
                      )}
                      <span>{theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}</span>
                    </button>

                    <div className="h-px bg-gray-200 dark:bg-gray-600 my-1"></div>

                    <button
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Cerrar Sesión</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg text-white hover:text-accent-200 hover:bg-white/10 transition-colors duration-200"
              onClick={toggleMobileMenu}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-primary-700 dark:bg-primary-800 border-t border-primary-800 dark:border-primary-600 animate-slide-in-left">
          <div className="px-4 py-4 space-y-3">
            {/* Mobile Menu Items */}
            {menuConfig && menuConfig.length > 0 && menuConfig.map((menu) => (
              <div key={menu.id} className="border-b border-primary-600 dark:border-primary-500 pb-3 mb-3 last:border-b-0 last:pb-0 last:mb-0">
                <DropdownMenu
                  menu={menu}
                  onMenuClick={handleMenuItemClick}
                  textColor="text-white"
                  hoverColor="hover:text-accent-200"
                  bgHover="hover:bg-white/10"
                  isMobile={true}
                />
              </div>
            ))}

            {/* Mobile User Section */}
            <div className="mt-6 pt-4 border-t border-primary-600 dark:border-primary-500">
              {/* User Info */}
              <div className="flex items-center space-x-3 px-3 py-3 bg-white/5 rounded-lg mb-4">
                <div className="w-12 h-12 bg-accent-500 rounded-full flex items-center justify-center text-white text-lg font-medium flex-shrink-0">
                  {getUserAvatar()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">
                    {getUserName()}
                  </div>
                  <div className="text-xs text-white/70 truncate">
                    {user?.email}
                  </div>
                  <div className="text-xs text-accent-300 font-medium">
                    {getUserRoleDisplay()}
                  </div>
                </div>
              </div>

              {/* Mobile Actions */}
              <div className="space-y-2">
                <button
                  onClick={handleThemeToggle}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-white hover:text-accent-200 hover:bg-white/10 transition-all duration-200"
                  title={theme === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
                >
                  {theme === 'dark' ? (
                    <Sun className="w-5 h-5 text-yellow-400" />
                  ) : (
                    <Moon className="w-5 h-5 text-blue-300" />
                  )}
                  <span className="font-medium">{theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500/10 transition-all duration-200"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Cerrar Sesión</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;